import { useEffect, useState, type ChangeEvent } from "react";
import { api } from "../lib/api";

interface SystemData {
  time: { utc: string; seoul: string; timezone: string };
  ntp: { managedBy: string; databaseOffsetMs: number | null; note: string };
  runtime: { node: string; uptimeSeconds: number; hostUptimeSeconds: number; totalMemoryBytes: number; freeMemoryBytes: number; loadAverage: number[] };
  session: { sessionTimeoutMinutes: number; maxLoginFailures: number; lockMinutes: number };
  recordingTimezone: string;
  displayTimezone: string;
}

export function SystemPage() {
  const [data, setData] = useState<SystemData | null>(null);
  const [message, setMessage] = useState("");
  useEffect(() => { void api<SystemData>("/api/system").then(setData); }, []);

  const backup = async () => {
    const response = await fetch("/api/system/backup", { credentials: "same-origin" });
    if (!response.ok) { setMessage("설정 백업 파일을 만들지 못했습니다."); return; }
    const link = document.createElement("a");
    link.href = URL.createObjectURL(await response.blob());
    link.download = `nvr-settings-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const restore = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !window.confirm("선택한 백업 설정을 현재 시스템에 복원합니까?")) return;
    try {
      await api("/api/system/restore", { method: "POST", body: await file.text() });
      setMessage("설정을 복원하고 미디어 서버에 반영했습니다.");
    } catch (error) { setMessage(error instanceof Error ? error.message : "복원 실패"); }
    event.target.value = "";
  };

  const saveSecurity = async () => {
    if (!data) return;
    try {
      await api("/api/system/security", { method: "PUT", body: JSON.stringify(data.session) });
      setMessage("보안 정책을 즉시 적용했습니다.");
    } catch (error) { setMessage(error instanceof Error ? error.message : "저장 실패"); }
  };

  if (!data) return <div className="loading-inline">시스템 정보를 불러오는 중…</div>;
  const used = data.runtime.totalMemoryBytes - data.runtime.freeMemoryBytes;
  return <>
    <div className="page-heading"><div><p className="eyebrow">시스템 설정</p><h2>시스템</h2></div></div>
    {message && <div className="alert alert-secondary">{message}</div>}
    <div className="row g-4">
      <div className="col-xl-6"><div className="panel">
        <div className="panel-title"><h3>시간 기준</h3></div>
        <dl className="system-list">
          <div><dt>서버 표준시(UTC)</dt><dd>{data.time.utc}</dd></div>
          <div><dt>한국 표준시</dt><dd>{data.time.seoul}</dd></div>
          <div><dt>녹화 시간 기준</dt><dd>{data.recordingTimezone}</dd></div>
          <div><dt>화면 표시 시간</dt><dd>{data.displayTimezone}</dd></div>
          <div><dt>시간 자동 동기화(NTP)</dt><dd>{data.ntp.managedBy}</dd></div>
          <div><dt>DB 시계 오차</dt><dd>{data.ntp.databaseOffsetMs === null ? "-" : `${data.ntp.databaseOffsetMs} ms`}</dd></div>
        </dl>
        <p className="small text-secondary mb-0 mt-2">시간 동기화는 DSM에서 관리하며 NVR은 NAS 시간을 사용합니다.</p>
      </div></div>
      <div className="col-xl-6"><div className="panel">
        <div className="panel-title"><h3>서버 상태</h3></div>
        <dl className="system-list">
          <div><dt>Node 버전</dt><dd>{data.runtime.node}</dd></div>
          <div><dt>API 작동 시간</dt><dd>{formatDuration(data.runtime.uptimeSeconds)}</dd></div>
          <div><dt>NAS 작동 시간</dt><dd>{formatDuration(data.runtime.hostUptimeSeconds)}</dd></div>
          <div><dt>메모리</dt><dd>{formatBytes(used)} / {formatBytes(data.runtime.totalMemoryBytes)}</dd></div>
          <div><dt>시스템 부하</dt><dd>{data.runtime.loadAverage.map((value) => value.toFixed(2)).join(" / ")}</dd></div>
        </dl>
      </div></div>
      <div className="col-xl-6"><div className="panel">
        <div className="panel-title"><h3>보안 정책</h3></div>
        <div className="row g-2">
          <div className="col-4"><label>자동 로그아웃 (분)</label><input className="form-control" type="number" min="5" max="1440" value={data.session.sessionTimeoutMinutes} onChange={(event) => setData({ ...data, session: { ...data.session, sessionTimeoutMinutes: Number(event.target.value) } })} /></div>
          <div className="col-4"><label>로그인 실패 허용 횟수</label><input className="form-control" type="number" min="3" max="20" value={data.session.maxLoginFailures} onChange={(event) => setData({ ...data, session: { ...data.session, maxLoginFailures: Number(event.target.value) } })} /></div>
          <div className="col-4"><label>계정 잠금 시간 (분)</label><input className="form-control" type="number" min="1" max="1440" value={data.session.lockMinutes} onChange={(event) => setData({ ...data, session: { ...data.session, lockMinutes: Number(event.target.value) } })} /></div>
        </div>
        <button className="btn btn-primary mt-3" onClick={() => void saveSecurity()}>정책 저장</button>
      </div></div>
      <div className="col-xl-6"><div className="panel">
        <div className="panel-title"><h3>설정 백업 및 복원</h3></div>
        <p className="text-secondary small">카메라, 사용자, 권한, 녹화, 저장소 설정을 포함합니다. 녹화 영상은 포함하지 않습니다.</p>
        <div className="d-flex gap-2"><button className="btn btn-primary" onClick={() => void backup()}><i className="bi bi-download me-2" />설정 백업 내려받기</button><label className="btn btn-outline-warning mb-0"><i className="bi bi-upload me-2" />백업 설정 복원<input type="file" accept="application/json" hidden onChange={(event) => void restore(event)} /></label></div>
      </div></div>
    </div>
  </>;
}

function formatDuration(value: number) { const days = Math.floor(value / 86400), hours = Math.floor(value % 86400 / 3600), minutes = Math.floor(value % 3600 / 60); return `${days}일 ${hours}시간 ${minutes}분`; }
function formatBytes(value: number) { return `${(value / 1024 ** 3).toFixed(1)} GB`; }
